import Vue from 'vue';
import Vuex from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import app from '../app/store/app';
import eps from '../pages/prizes/store/eps';
import payments from '../pages/prizes/store/payments';
import banners from '../pages/profile/store/banners';
import bonuses from '../pages/profile/store/bonuses';
import surveys from '../pages/profile/store/surveys';
import webpush from '../pages/profile/store/webpush';
import courses from '../pages/courses/store/courses';
import notifications from '../pages/profile/store/notifications';
import mobileNotifications from '../pages/mobileNotifications/store/mobileNotifications';
import sales from '../pages/sales/store/sales';
import news from '../pages/news/store/news';
import tickets from '../pages/tickets/store/tickets';
import shop from '../pages/shop/store/shop';
import faq from '../pages/faq/store/faq';

// eslint-disable-next-line import/no-cycle
import { ax, axf, axl } from '../customAxios';

Vue.use(Vuex);

const store = new Vuex.Store({
  plugins: [createPersistedState({ key: 'promo', ssr: false })],
  state: {
    settingsError: null,
    settingsReady: false,
    loading: false,
    dialogs: {
      surveys: true,
      notifications: true,
    },
    profileError: null,
    profile_id: null,
    token: null,
    user: null,
    userTransactions: [],
    purses: [],
    paymentsInfo: {},
    settings: {
      register_rules: true,
      register_pers: true,
    },
    agreedRules: false,
  },
  mutations: {
    SetSettingsError(state, message) { state.settingsError = message; },
    SetSettingsReady(state, ready) { state.settingsReady = ready; },
    SetSettings(state, settings) {
      state.settings = settings;
    },
    SetProfileError(state, message) { state.profileError = message; },
    SetUser(state, payload) {
      state.profileError = null;
      state.user = payload;
    },
    SetPurses(state, payload) {
      state.purses = payload;
    },
    SetDialog(state, payload) {
      state.dialogs[payload.dialog] = payload.value;
    },
    SetUserTransactions(state, payload) {
      state.userTransactions = payload;
    },
    SetLogged(state, payload) {
      state.profile_id = payload.profile_id;
      state.token = payload.token;
    },
    SetLoading(state, payload) {
      state.loading = payload;
    },
    SetPaymentsSettings(state, payload) {
      state.paymentsInfo.methods = payload;
    },
    InitialStore(state) {
      state.loading = false;
      state.settingsReady = false;
      state.settingsError = null;
      state.profileError = null;
      state.agreedRules = false;
      state.purses = [];
      state.dialogs = {
        surveys: true,
        notifications: true,
      };
      state.profile_id = null;
      state.token = null;
      state.user = null;
      state.userTransactions = [];
      state.paymentsInfo = {};
      state.settings = {};
    },
    Logout(state) {
      state.profile_id = null;
      state.user = null;
      state.token = null;
    },
    SetAgreedRules(state, payload) {
      state.agreedRules = payload;
    },
  },
  actions: {
    SetLoading({ commit }, payload) {
      commit('SetLoading', payload);
    },
    UnsetError() {
      Vue.notify({ group: 'error', clean: true });
    },
    UnsetInfo() {
      Vue.notify({ group: 'info', clean: true });
    },
    ShowInfo({ dispatch }, message) {
      dispatch('UnsetError');
      dispatch('UnsetInfo');
      dispatch('ShowInfoMessage', message);
    },
    ShowError({ dispatch }, message) {
      dispatch('UnsetError');
      dispatch('UnsetInfo');
      dispatch('ShowErrorMessage', message);
    },
    ShowErrorMessage({}, message) {
      const text = message;
      Vue.notify({ group: 'error', type: 'error', text, duration: 60000 });
    },
    ShowInfoMessage({}, message) {
      const text = message;
      Vue.notify({ group: 'info', type: 'info', text, duration: 7000 });
    },
    HandleError({ dispatch }, error) {
      if (error.response) {
        const { data } = error.response;
        let errors = [];
        if (data && typeof data === 'object' && 'error' in data) {
          errors = [data.error];
        } else if (data && typeof data === 'object' && data.errors) {
          if (Array.isArray(data.errors)) {
            ({ errors } = data);
          } else if (typeof data.errors === 'object') {
            Object.keys(data.errors).forEach((k) => {
              errors.push(data.errors[k]);
            });
          } else {
            errors = [data.errors];
          }
        }
        if (!errors.length) errors = ['Что-то пошло не так'];
        errors.slice(0, 2).forEach((message) => {
          dispatch('ShowErrorMessage', message);
        });
      } else if (error.request) {
        dispatch('ShowErrorMessage', 'С вашим подключением не все так просто. Пожалуйста, проверьте доступ к интернету');
      } else {
        dispatch('ShowErrorMessage', 'Что-то пошло не так');
      }
    },
    async GetSettings({ commit, getters }) {
      commit('SetSettingsReady', false);
      commit('SetSettingsError', null);
      try {
        const response = await getters.ax.post('api/settings');
        if (!response || !response.data || !response.data.settings) {
          throw new Error('Settings are missing from the response');
        }
        commit('SetSettings', response.data.settings);
        commit('SetSettingsReady', true);
        return response.data;
      } catch (error) {
        commit('SetSettingsError', 'Не удалось загрузить настройки кабинета');
        throw error;
      }
    },
    async GetProfile({ commit, state, getters, dispatch }) {
      commit('SetProfileError', null);
      const profileId = state.profile_id;
      const { token } = state;
      if (!profileId) return null;
      const sameSession = () => state.profile_id === profileId && state.token === token;
      try {
        const response = await getters.ax.post('profiles/api/auth/get-profile', {
          profile_id: profileId,
        });
        // A response from the previous session must not repopulate a logged-out account.
        if (!sameSession()) return null;
        const profile = response && response.data && response.data.profile;
        if (!profile || !Array.isArray(profile.purses)) {
          throw new Error('Profile or purses are missing from the response');
        }
        if (profile.blocked_at) {
          await dispatch('UserLogout');
          dispatch('ShowInfoMessage', 'Ваша учётная запись заблокирована');
          return null;
        }
        commit('SetUser', profile);
        commit('SetPurses', profile.purses.filter(item => item.balance > 0));
        return response.data;
      } catch (error) {
        if (!sameSession()) return null;
        commit('SetProfileError', 'Не удалось загрузить профиль');
        throw error;
      }
    },
    CheckAgreedRules({ commit, state, getters }) {
      return getters.axl.post('api/conditions/check-agreeing-rules', { profile_id: state.profile_id })
        .then((response) => {
          commit('SetAgreedRules', response.data.agreed);
        });
    },
    async GetTransactions({ commit, state, getters }) {
      if (!state.profile_id) return null;
      const response = await getters.axl.post('profiles/api/transaction/list', {
        profile_id: state.profile_id,
      });
      if (!response || !response.data) throw new Error('Transactions response is missing');
      commit('SetUserTransactions', response.data.transactions);
      return response.data;
    },
    async GetPaymentsSettings({ commit, state, getters }) {
      if (!state.profile_id) return null;
      const response = await getters.ax.post('payments/api-v3/settings/view');
      if (!response || !response.data) throw new Error('Payments settings response is missing');
      commit('SetPaymentsSettings', response.data.settings);
      return response.data;
    },
    UserLogin({ commit, dispatch }, payload) {
      commit('SetLogged', payload);
      dispatch('GetProfile').catch(error => dispatch('HandleError', error));
      dispatch('ShowInfoMessage', 'Добро пожаловать!');
    },
    UserLogout({ commit, dispatch }) {
      commit('Logout');
      commit('InitialStore');
      dispatch('courses/InitialStoreCourses');
      dispatch('mobileNotifications/InitialStoreNotifications');
      dispatch('news/InitialStoreNews');
      dispatch('faq/InitialStoreFaq');
      dispatch('eps/InitialStoreEps');
      dispatch('payments/InitialStorePayments');
      dispatch('banners/InitialStoreBanners');
      dispatch('bonuses/InitialStoreBonuses');
      dispatch('notifications/InitialStoreNotifications');
      dispatch('surveys/InitialStoreSurveys');
      dispatch('sales/InitialStoreSales');
      dispatch('shop/InitialStateShop');
      dispatch('tickets/InitialStoreTickets');
      dispatch('webpush/ResetToken');
      localStorage.clear();
      return dispatch('GetSettings').catch(error => dispatch('HandleError', error));
    },
    DownloadFile({ getters, dispatch }, info) {
      getters.axf.post(info.url, info.data).then((response) => {
        if (navigator.msSaveBlob) {
          navigator.msSaveBlob(new Blob([response.data]), info.filename);
        } else {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', info.filename);
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
          // Let the browser process the download click before releasing its URL.
          setTimeout(() => window.URL.revokeObjectURL(url), 0);
        }
      }).catch((error) => {
        dispatch('HandleError', error);
      });
    },
    AgreeingRules({ state, getters, dispatch, commit }) {
      getters.axl.post('api/conditions/agreeing-rules', { profile_id: state.profile_id })
        .then(() => {
          commit('SetAgreedRules', true);
        })
        .catch((error) => {
          dispatch('HandleError', error);
        });
    },
  },
  getters: {
    isApproved(state) {
      return !!(state.user && state.user.is_checked && state.user.blocked_at === null);
    },
    cartCount(state) {
      return state.eps.cartLength + state.shop.cart.cartLength;
    },
    ax,
    axl,
    axf,
    token(state) {
      return state.token;
    },
    isAuthenticated(state) {
      return state.profile_id !== null && state.profile_id !== undefined
        && state.token !== null && state.token !== undefined;
    },
  },
  modules: {
    app,
    courses,
    eps,
    payments,
    surveys,
    notifications,
    mobileNotifications,
    sales,
    news,
    tickets,
    banners,
    bonuses,
    shop,
    webpush,
    faq,
  },
});

export default store;
